import {
  KeyboardEvent,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {ClientAnalytics, Link, useServerProps, useUrl} from '@shopify/hydrogen';
import classNames from 'classnames';

import SearchToken from '../search/SearchToken.client';
import SearchSuggestionList from '../search/SearchSuggestionList.client';
import PopularSearchList from '../search/PopularSearchList.client';
import ProductTile from '../ProductTile.client';
import {useSearchUI} from '../../contexts/SearchUIProvider.client';
import {Product} from '@shopify/hydrogen/storefront-api-types';
import ToggleSwitchField from '../formControls/ToggleSwitchField.client';
import {TwlAnalyticsEvent} from '../../constants';
import VectorIcon from '../VectorIcon';
import {
  parseSearchTokens,
  sanitiseSearchValue,
  stringifySearchTokens,
} from '../../utils/searchTokens';

const popularSearchTokens = [
  'Single Malt',
  'From $100 to $150',
  'Australia',
  'About 15 Years Old',
];

const minTokenLength = 3;

interface LiveSearchModalProps {
  products: Array<Product>;
}

export default function LiveSearchModal({products}: LiveSearchModalProps) {
  const {href, searchParams} = useUrl();
  const {
    includeOutOfStock,
    setIncludeOutOfStock,
    isSearchOpen,
    closeSearch,
    inTwlShop,
    setInTwlShop,
  } = useSearchUI();

  useEffect(() => {
    if (isSearchOpen) {
      setIncludeOutOfStock(includeOutOfStock);
    }
  }, [isSearchOpen, includeOutOfStock, setIncludeOutOfStock]);

  // Reset the includeOutOfStock state when the search modal is closed
  useEffect(() => {
    if (!isSearchOpen) {
      setIncludeOutOfStock(false);
    }
  }, [isSearchOpen, setIncludeOutOfStock]);

  const [searchTokens, _setSearchTokens] = useState<Array<string>>(
    parseSearchTokens(searchParams.get('searchTokens')),
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState<string>('');

  const {pending, setServerProps} = useServerProps();

  const sanitisedSearchValue = useMemo(
    () => sanitiseSearchValue(searchValue),
    [searchValue],
  );

  // autofocus the search input on display
  useEffect(
    function () {
      if (isSearchOpen) {
        // delay 100ms to allow the modal to become visible
        setTimeout(function () {
          inputRef.current.focus();
        }, 100);
      }
    },
    [isSearchOpen],
  );

  const [hideSearchSuggestions, setHideSearchSuggestions] = useState<boolean>();

  useEffect(() => {
    setHideSearchSuggestions(document.activeElement === inputRef.current);
  }, []);

  const handleInputFocus = useCallback(
    () => setHideSearchSuggestions(false),
    [],
  );

  const searchBarRef = useRef<HTMLDivElement>(null);
  const handleClickOutside = (event: MouseEvent | TouchEvent) => {
    if (
      searchBarRef.current &&
      !searchBarRef.current.contains(event.target as Node)
    ) {
      setHideSearchSuggestions(true);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside); // Mobile
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside); // Mobile
    };
  }, []);

  useEffect(closeSearch, [closeSearch, href]);

  const setSearchTokens = useCallback(
    (tokens: string[]) => {
      _setSearchTokens(tokens);
      setServerProps('searchTokens', tokens);
    },
    [setServerProps],
  );

  useEffect(() => {
    if (searchTokens.length > 0) {
      ClientAnalytics.publish(TwlAnalyticsEvent.LIVE_SEARCH, true, {
        ...ClientAnalytics.getPageAnalyticsData(),
        searchTokens,
      });
    }
  }, [searchTokens]);

  const addToken = useCallback(
    (token: string, clear = false) => {
      if (token && !searchTokens.includes(token)) {
        setSearchTokens(searchTokens.concat([token]));
      }
      if (clear) {
        setSearchValue('');
      }
    },
    [searchTokens, setSearchTokens],
  );

  const searchSuggestionListOnClickHandler = useCallback(
    (token) => {
      addToken(sanitiseSearchValue(token), true);
    },
    [addToken],
  );

  const popularSearchListOnClickHandler = useCallback(
    (token) => addToken(sanitiseSearchValue(token)),
    [addToken],
  );

  const removeToken = useCallback(
    (position: number) => {
      setSearchTokens(searchTokens.filter((_, index) => index !== position));
    },
    [searchTokens, setSearchTokens],
  );

  const resetSearch = useCallback(() => {
    setSearchTokens([]);
    setSearchValue('');
    setIncludeOutOfStock(false);
    setInTwlShop(false);
    inputRef.current.focus();
  }, [setSearchTokens, setIncludeOutOfStock, setInTwlShop]);

  const handleTokenEdit = useCallback(
    (position: number, searchToken: string) => {
      if (
        sanitisedSearchValue &&
        !searchTokens.includes(sanitisedSearchValue)
      ) {
        const newTokens = searchTokens.filter((_, index) => index !== position);
        if (sanitisedSearchValue.length >= minTokenLength) {
          newTokens.push(sanitisedSearchValue);
        }
        setSearchTokens(newTokens);
      } else {
        removeToken(position);
      }
      setSearchValue(searchToken);
    },
    [removeToken, sanitisedSearchValue, searchTokens, setSearchTokens],
  );

  const inputKeyDownHandler = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (isSearchOpen && event.key == 'Escape') {
        closeSearch();
      }
      // start editing the last token on backspace when the input is empty
      if (
        event.key === 'Backspace' &&
        searchTokens.length &&
        searchValue === ''
      ) {
        event.preventDefault();
        handleTokenEdit(
          searchTokens.length - 1,
          searchTokens[searchTokens.length - 1],
        );
      }
      // add a new token
      if (event.key === 'Enter') {
        addToken(sanitisedSearchValue, true);
      }
      // Hide suggestions on 'Tab'
      if (event.key === 'Tab') {
        setHideSearchSuggestions(true);
      }
    },
    [
      addToken,
      closeSearch,
      handleTokenEdit,
      isSearchOpen,
      sanitisedSearchValue,
      searchTokens,
      searchValue,
    ],
  );

  const inputChangeHandler: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => setSearchValue(event.target.value),
    [],
  );

  const navigateHandler = useCallback(() => closeSearch(), [closeSearch]);

  const viewAllLink = useMemo(() => {
    return `/shop?${new URLSearchParams({
      searchTokens: stringifySearchTokens(searchTokens),
    })}${includeOutOfStock ? '&inStock=any' : ''}&inTwlShop=${inTwlShop}`;
  }, [searchTokens, includeOutOfStock, inTwlShop]);

  return (
    <div className="livesearch" id="livesearch">
      <div className="container livesearch__container">
        <div ref={searchBarRef} className="livesearch__top">
          <div
            role="button"
            tabIndex={-1}
            className="livesearch__return"
            title="Back to Website"
            onClick={closeSearch}
          >
            <VectorIcon iconName="left-arrow" />
          </div>
          <div className="livesearch__tokens" id="livesearch-tokens">
            {searchTokens.map((searchToken, index) => (
              <SearchToken
                key={searchToken}
                token={searchToken}
                onRemove={() => removeToken(index)}
                onEdit={() => handleTokenEdit(index, searchToken)}
              />
            ))}
          </div>
          <div className="livesearch__form" id="livesearch-form">
            {/* <div className="livesearch__form-col"> */}
            <input
              autoComplete="off"
              type="search"
              className="livesearch__input"
              id="livesearch-input"
              placeholder="Search for whisky &amp; more..."
              onKeyDown={inputKeyDownHandler}
              onChange={inputChangeHandler}
              value={searchValue}
              ref={inputRef}
              onFocus={handleInputFocus}
            />
            {/* </div> */}
          </div>
          <div
            role="button"
            tabIndex={-1}
            className="livesearch__del"
            title="Reset Search"
            onClick={resetSearch}
          >
            <VectorIcon iconName="close-icon" />
          </div>
          <SearchSuggestionList
            searchTerm={sanitisedSearchValue}
            onClick={searchSuggestionListOnClickHandler}
            hide={hideSearchSuggestions}
            includeOutOfStock={includeOutOfStock}
          />
        </div>
        <div
          className="out-of-stock-option toggle-switch-row"
          style={{marginTop: '15px', display: 'flex', justifyContent: 'left'}}
        >
          <ToggleSwitchField
            name="includeOutOfStock"
            checked={includeOutOfStock}
            onChange={() => setIncludeOutOfStock(!includeOutOfStock)}
          >
            Out of stock
          </ToggleSwitchField>
          <ToggleSwitchField
            name="inTwlShop"
            checked={inTwlShop}
            onChange={() => setInTwlShop(!inTwlShop)}
          >
            TWL Shop
          </ToggleSwitchField>
        </div>

        <PopularSearchList
          hidden={searchTokens.length > 0 || sanitisedSearchValue !== ''}
          tokens={popularSearchTokens}
          onClick={popularSearchListOnClickHandler}
        />

        <div
          className={classNames('livesearch__products products-list', {
            showProducts: searchTokens.length > 0,
          })}
          id="livesearch-products"
        >
          {!pending && products.length === 0 ? (
            <div className="product js-product" style={{marginTop: '20px'}}>
              <p>No products to show</p>
            </div>
          ) : (
            products.map((product) => (
              <ProductTile
                key={product.handle}
                product={product}
                onNavigate={navigateHandler}
              />
            ))
          )}
        </div>
        {!pending && products.length > 0 && (
          <Link
            to={viewAllLink}
            onClick={closeSearch}
            className="livesearch__view-all"
          >
            View all results
          </Link>
        )}
      </div>
    </div>
  );
}
